/**
 * Timer Calculation Utilities
 * 
 * Funções reutilizáveis para cálculos de horas trabalhadas.
 * Estas funções serão utilizadas futuramente para inteligência financeira.
 * 
 * NOTA: Nenhum dado é salvo no banco - todos os cálculos são feitos em memória.
 */

import { supabase } from '@/integrations/supabase/client';
import { startOfMonth, endOfMonth, format } from 'date-fns';

export interface TimeEntry {
  id?: string;
  duration_minutes: number | null;
  is_billable: boolean | null;
  hourly_rate: number | null;
  category?: string | null;
  project_id?: string | null;
}

export interface HoursSummary {
  totalMinutes: number;
  totalHours: number;
  billableMinutes: number;
  billableHours: number;
  nonBillableMinutes: number;
  nonBillableHours: number;
}

export interface MonthlyStats {
  totalHours: number;
  billableHours: number;
  nonBillableHours: number;
  billableValue: number;
  effectiveHourlyRate: number | null;
}

// ============================================
// FUNÇÕES DE CÁLCULO LOCAL (sem fetch)
// ============================================

/**
 * Calcula resumo de horas a partir de um array de entries
 */
export function calculateHoursSummary(entries: TimeEntry[]): HoursSummary {
  const totalMinutes = entries.reduce((acc, entry) => acc + (entry.duration_minutes || 0), 0);
  
  const billableMinutes = entries
    .filter(entry => entry.is_billable !== false)
    .reduce((acc, entry) => acc + (entry.duration_minutes || 0), 0);
  
  const nonBillableMinutes = totalMinutes - billableMinutes;

  return {
    totalMinutes,
    totalHours: Math.round((totalMinutes / 60) * 100) / 100,
    billableMinutes,
    billableHours: Math.round((billableMinutes / 60) * 100) / 100,
    nonBillableMinutes,
    nonBillableHours: Math.round((nonBillableMinutes / 60) * 100) / 100,
  };
}

/**
 * Calcula valor total faturável a partir de um array de entries
 */
export function calculateBillableValue(entries: TimeEntry[]): number {
  return entries
    .filter(entry => entry.is_billable !== false && entry.hourly_rate)
    .reduce((acc, entry) => {
      const hours = (entry.duration_minutes || 0) / 60;
      const rate = entry.hourly_rate || 0;
      return acc + (hours * rate);
    }, 0);
}

/**
 * Calcula taxa horária efetiva (receita / horas trabalhadas)
 */
export function calculateEffectiveHourlyRate(
  totalRevenue: number, 
  totalBillableHours: number
): number | null {
  if (totalBillableHours <= 0) return null;
  return Math.round((totalRevenue / totalBillableHours) * 100) / 100;
}

/**
 * Agrupa horas por categoria
 */
export function groupHoursByCategory(entries: TimeEntry[]): Record<string, number> {
  const groups: Record<string, number> = {};
  
  entries.forEach(entry => {
    const category = entry.category || 'dev';
    const hours = (entry.duration_minutes || 0) / 60;
    groups[category] = (groups[category] || 0) + hours;
  });
  
  // Arredondar valores
  Object.keys(groups).forEach(key => {
    groups[key] = Math.round(groups[key] * 100) / 100;
  });
  
  return groups;
}

/**
 * Agrupa horas por projeto
 */
export function groupHoursByProject(entries: TimeEntry[]): Record<string, number> {
  const groups: Record<string, number> = {};
  
  entries.forEach(entry => {
    const projectId = entry.project_id || 'sem_projeto';
    const hours = (entry.duration_minutes || 0) / 60;
    groups[projectId] = (groups[projectId] || 0) + hours;
  });
  
  // Arredondar valores
  Object.keys(groups).forEach(key => {
    groups[key] = Math.round(groups[key] * 100) / 100;
  });
  
  return groups;
}

// ============================================
// FUNÇÕES ASYNC (com fetch do banco)
// ============================================

/**
 * Busca e calcula total de horas do mês atual para um usuário
 */
export async function fetchMonthlyHoursTotal(userId: string): Promise<number> {
  const now = new Date();
  const monthStart = format(startOfMonth(now), 'yyyy-MM-dd');
  const monthEnd = format(endOfMonth(now), 'yyyy-MM-dd');

  const { data, error } = await supabase
    .from('time_entries')
    .select('duration_minutes')
    .eq('user_id', userId)
    .gte('start_time', `${monthStart}T00:00:00`)
    .lt('start_time', `${format(new Date(new Date(monthEnd).setDate(new Date(monthEnd).getDate() + 1)), 'yyyy-MM-dd')}T00:00:00`);

  if (error) throw error;

  const totalMinutes = data?.reduce((acc, entry) => acc + (entry.duration_minutes || 0), 0) || 0;
  return Math.round((totalMinutes / 60) * 100) / 100;
}

/**
 * Busca e calcula horas faturáveis do mês atual
 */
export async function fetchMonthlyBillableHours(userId: string): Promise<number> {
  const now = new Date();
  const monthStart = format(startOfMonth(now), 'yyyy-MM-dd');
  const monthEnd = format(endOfMonth(now), 'yyyy-MM-dd');

  const { data, error } = await supabase
    .from('time_entries')
    .select('duration_minutes')
    .eq('user_id', userId)
    .eq('is_billable', true)
    .gte('start_time', `${monthStart}T00:00:00`)
    .lt('start_time', `${format(new Date(new Date(monthEnd).setDate(new Date(monthEnd).getDate() + 1)), 'yyyy-MM-dd')}T00:00:00`);

  if (error) throw error;

  const totalMinutes = data?.reduce((acc, entry) => acc + (entry.duration_minutes || 0), 0) || 0;
  return Math.round((totalMinutes / 60) * 100) / 100;
}

/**
 * Busca e calcula horas não faturáveis do mês atual
 */
export async function fetchMonthlyNonBillableHours(userId: string): Promise<number> {
  const now = new Date();
  const monthStart = format(startOfMonth(now), 'yyyy-MM-dd');
  const monthEnd = format(endOfMonth(now), 'yyyy-MM-dd');

  const { data, error } = await supabase
    .from('time_entries')
    .select('duration_minutes')
    .eq('user_id', userId)
    .eq('is_billable', false)
    .gte('start_time', `${monthStart}T00:00:00`)
    .lt('start_time', `${format(new Date(new Date(monthEnd).setDate(new Date(monthEnd).getDate() + 1)), 'yyyy-MM-dd')}T00:00:00`);

  if (error) throw error;

  const totalMinutes = data?.reduce((acc, entry) => acc + (entry.duration_minutes || 0), 0) || 0;
  return Math.round((totalMinutes / 60) * 100) / 100;
}

/**
 * Calcula taxa horária real baseada em receita total do mês
 * @param userId ID do usuário
 * @param monthlyRevenue Receita total do mês (obtida da tabela de pagamentos)
 */
export async function calculateRealHourlyRate(
  userId: string, 
  monthlyRevenue: number
): Promise<number | null> {
  const billableHours = await fetchMonthlyBillableHours(userId);
  
  if (billableHours <= 0) return null;
  
  return Math.round((monthlyRevenue / billableHours) * 100) / 100;
}

/**
 * Busca estatísticas completas do mês atual
 */
export async function fetchMonthlyStats(userId: string): Promise<MonthlyStats> {
  const now = new Date();
  const monthStart = format(startOfMonth(now), 'yyyy-MM-dd');
  const monthEnd = format(endOfMonth(now), 'yyyy-MM-dd');

  const { data, error } = await supabase
    .from('time_entries')
    .select('duration_minutes, is_billable, hourly_rate')
    .eq('user_id', userId)
    .gte('start_time', `${monthStart}T00:00:00`)
    .lt('start_time', `${format(new Date(new Date(monthEnd).setDate(new Date(monthEnd).getDate() + 1)), 'yyyy-MM-dd')}T00:00:00`);

  if (error) throw error;

  const entries = data || [];
  const summary = calculateHoursSummary(entries as TimeEntry[]);
  const billableValue = calculateBillableValue(entries as TimeEntry[]);
  const effectiveRate = calculateEffectiveHourlyRate(billableValue, summary.billableHours);

  return {
    totalHours: summary.totalHours,
    billableHours: summary.billableHours,
    nonBillableHours: summary.nonBillableHours,
    billableValue,
    effectiveHourlyRate: effectiveRate,
  };
}

// ============================================
// HELPERS DE FORMATAÇÃO
// ============================================

/**
 * Formata minutos em string legível (ex: "2h 30min")
 */
export function formatMinutesToReadable(minutes: number): string {
  if (minutes <= 0) return '0min';
  
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  
  if (hours === 0) return `${mins}min`;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}min`;
}

/**
 * Formata horas em string legível (ex: "2h 30min")
 */
export function formatHoursToReadable(hours: number): string {
  const totalMinutes = Math.round(hours * 60);
  return formatMinutesToReadable(totalMinutes);
}

/**
 * Formata valor monetário em BRL
 */
export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

// ============================================
// VERIFICAÇÃO DE PLANO (PREPARAÇÃO)
// ============================================

export interface PlanAccess {
  canCreateEntry: boolean;
  canEditEntry: boolean;
  canExportData: boolean;
  reason?: string;
}

/**
 * Verifica acesso baseado no plano do usuário
 * NOTA: Esta função apenas prepara a estrutura - não bloqueia UI ainda
 */
export function checkPlanAccess(
  planStatus: 'trial' | 'pro', 
  trialExpired: boolean
): PlanAccess {
  // Pro users have full access
  if (planStatus === 'pro') {
    return {
      canCreateEntry: true,
      canEditEntry: true,
      canExportData: true,
    };
  }

  // Trial users with active trial have full access
  if (planStatus === 'trial' && !trialExpired) {
    return {
      canCreateEntry: true,
      canEditEntry: true,
      canExportData: true,
    };
  }

  // Expired trial - limited access (estrutura preparada para bloqueio futuro)
  return {
    canCreateEntry: false,
    canEditEntry: false,
    canExportData: false,
    reason: 'Seu período de teste expirou. Ative o plano Pro para continuar.',
  };
}
